import {Moment} from 'moment';

import {Timezone} from 'common/constants';
import {Translatable} from 'common/i18n';
import {LocalizationStore} from 'common/i18n/store';
import {TimePeriodSettings} from 'common/report/stores/types';
import {DEFAULT_TIME_PERIOD_SETTINGS, getLocalTimezone, NUMBER_OF_DAYS_IN_UNIT} from 'common/util/date';

import {END_OF_TODAY, PERIOD_PRESET_UNITS, unitToPeriodToDateAttributes} from './presets';
import {
  DateRangeAttributes,
  DateRangeType,
  ExclusionOffset,
  ExclusionPreset,
  INCOMPLETE_PERIOD_OFFSET,
  RelativeDate,
  StandardTimeUnit,
  TimeUnit,
  TimeUnitLabelTranslatable,
} from './types';
import {getNumberOfCalendarDays, getUnitStartTooltip} from './utils';

// Used for unit-less contexts where incomplete period is never compatible (ie dashboards, Events Report, etc)
export const ORDERED_EXCLUSION_PRESETS: ExclusionPreset[] = [
  ExclusionPreset.Today,
  ExclusionPreset.CurrentWeek,
  ExclusionPreset.CurrentMonth,
  ExclusionPreset.CurrentQuarter,
];

// Used for reports that can support unit controls, such as Insights, Funnels, etc.
export const ORDERED_EXCLUSION_PRESETS_WITH_INCOMPLETE_PERIOD: ExclusionPreset[] = [
  ...ORDERED_EXCLUSION_PRESETS,
  ExclusionPreset.IncompletePeriod,
];

export const EXCLUSION_PRESET_TO_OFFSET: {
  [key in ExclusionPreset]: ExclusionOffset | null;
} = {
  [ExclusionPreset.Custom]: null,
  [ExclusionPreset.NoExclusion]: null,
  [ExclusionPreset.Today]: {value: 1, unit: TimeUnit.Day},
  [ExclusionPreset.CurrentWeek]: {value: 1, unit: TimeUnit.Week},
  [ExclusionPreset.CurrentMonth]: {value: 1, unit: TimeUnit.Month},
  [ExclusionPreset.CurrentQuarter]: {value: 1, unit: TimeUnit.Quarter},
  [ExclusionPreset.IncompletePeriod]: INCOMPLETE_PERIOD_OFFSET,
};

export const SUPPORTED_EXCLUSION_OFFSET_UNITS: TimeUnit[] = [
  TimeUnit.Minute,
  TimeUnit.Hour,
  TimeUnit.Day,
  TimeUnit.Week,
  TimeUnit.Month,
  TimeUnit.Quarter,
];

export function getExclusionDisabledReason(
  exclusion: ExclusionPreset,
  isOvertimeChart: boolean,
  dateRange: DateRangeAttributes | null,
  asOfDate: Moment,
  projectTz: Timezone | undefined,
  timePeriodSettings: TimePeriodSettings | undefined,
): Translatable | null {
  if (exclusion === ExclusionPreset.NoExclusion) {
    return null;
  }

  if (!dateRange) {
    return ({t}) => t(`reports.date-range.exclusion.disabled.default-dashboard-date-range`);
  }

  // We don't allow excluding for between date ranges other than the TODAY Preset.
  if (dateRange.type === DateRangeType.Between && dateRange.to !== END_OF_TODAY) {
    return ({t}) => t(`reports.date-range.exclusion.disabled.between-date-range`);
  }
  if (exclusion === ExclusionPreset.IncompletePeriod) {
    if (!isOvertimeChart) {
      return ({t}) => t(`reports.date-range.exclusion.disabled.non-line-incomplete-period`);
    }
    return null;
  }

  const exclusionOffset = EXCLUSION_PRESET_TO_OFFSET[exclusion];
  const exclusionUnit = (exclusionOffset as RelativeDate).unit as StandardTimeUnit;

  let exclusionNumDays = 0;
  if (PERIOD_PRESET_UNITS.includes(exclusionUnit)) {
    // For accuracy as to current day, use PTD date ranges to calculate the exclusion offset.
    const ptdAttributes: DateRangeAttributes = unitToPeriodToDateAttributes(exclusionUnit);
    exclusionNumDays = getNumberOfCalendarDays(
      ptdAttributes,
      asOfDate,
      projectTz ?? getLocalTimezone(),
      timePeriodSettings ?? DEFAULT_TIME_PERIOD_SETTINGS,
    );
  } else {
    exclusionNumDays = NUMBER_OF_DAYS_IN_UNIT[(exclusionOffset as RelativeDate).unit as StandardTimeUnit];
  }
  const dateRangeNumDays = getNumberOfCalendarDays(
    dateRange,
    asOfDate,
    projectTz ?? getLocalTimezone(),
    timePeriodSettings ?? DEFAULT_TIME_PERIOD_SETTINGS,
  );
  if (exclusionNumDays >= dateRangeNumDays) {
    return ({t}) => t(`reports.date-range.exclusion.disabled.exclusion-exceeds-date-range`);
  }

  return null;
}

/* keep in sync with EXCLUSION_PRESET_TO_OFFSET and time clause parsing in date_range.py's DateRange.from_dict*/
export function exclusionOffsetToPreset(offset: ExclusionOffset | null | undefined): ExclusionPreset {
  if (!offset) {
    return ExclusionPreset.NoExclusion;
  } else if (offset === INCOMPLETE_PERIOD_OFFSET) {
    return ExclusionPreset.IncompletePeriod;
  } else if (typeof offset === `string`) {
    throw new Error(`invalid exclusion offset ${offset}. only $incomplete_period or relative dates are accepted.`);
  }

  if (offset.value === 1) {
    if (offset.unit === TimeUnit.Day) {
      return ExclusionPreset.Today;
    } else if (offset.unit === TimeUnit.Week) {
      return ExclusionPreset.CurrentWeek;
    } else if (offset.unit === TimeUnit.Month) {
      return ExclusionPreset.CurrentMonth;
    } else if (offset.unit === TimeUnit.Quarter) {
      return ExclusionPreset.CurrentQuarter;
    }
  }

  // Custom exclusion window
  return ExclusionPreset.Custom;
}

export const EXCLUSION_MENU_ITEM_LABEL: {[key in ExclusionPreset]: Translatable} = {
  [ExclusionPreset.Custom]: () => ``, // not a valid menu item. TODO: implement UX entry point for custom exclusion
  [ExclusionPreset.NoExclusion]: () => ``, // deselection state. not a valid menu item.
  [ExclusionPreset.Today]: ({t}) => t(`reports.date-range.exclusion.today.menu-item-label`),
  [ExclusionPreset.CurrentWeek]: ({t}) => t(`reports.date-range.exclusion.current-week.menu-item-label`),
  [ExclusionPreset.CurrentMonth]: ({t}) => t(`reports.date-range.exclusion.current-month.menu-item-label`),
  [ExclusionPreset.CurrentQuarter]: ({t}) => t(`reports.date-range.exclusion.current-quarter.menu-item-label`),
  [ExclusionPreset.IncompletePeriod]: ({t}) => t(`reports.date-range.exclusion.incomplete-period.menu-item-label`),
};

export const EXCLUSION_SELECTED_LABEL: {
  [key in ExclusionPreset]: (i18n: LocalizationStore, window?: RelativeDate) => string;
} = {
  [ExclusionPreset.Custom]: ({t, print}, window) => {
    if (!window) {
      return ``;
    }
    return t(`reports.date-range.exclusion.custom.selected-label`, {
      unit: print(TimeUnitLabelTranslatable[window.unit](window.value)).toLowerCase(),
      value: window.value,
    });
  },
  [ExclusionPreset.NoExclusion]: ({t}, _) => t(`reports.date-range.exclusion.title`),
  [ExclusionPreset.Today]: ({t}, _) => t(`reports.date-range.exclusion.today.selected-label`),
  [ExclusionPreset.CurrentWeek]: ({t}, _) => t(`reports.date-range.exclusion.current-week.selected-label`),
  [ExclusionPreset.CurrentMonth]: ({t}, _) => t(`reports.date-range.exclusion.current-month.selected-label`),
  [ExclusionPreset.CurrentQuarter]: ({t}, _) => t(`reports.date-range.exclusion.current-quarter.selected-label`),
  [ExclusionPreset.IncompletePeriod]: ({t}, _) => t(`reports.date-range.exclusion.incomplete-period.selected-label`),
};

export function getExclusionTooltip(preset: ExclusionPreset, timePeriodSettings: TimePeriodSettings): Translatable {
  switch (preset) {
    case ExclusionPreset.CurrentWeek:
      return getUnitStartTooltip(TimeUnit.Week, timePeriodSettings);
    case ExclusionPreset.CurrentQuarter:
      return getUnitStartTooltip(TimeUnit.Quarter, timePeriodSettings);
    case ExclusionPreset.IncompletePeriod:
      return ({t}) => t(`reports.date-range.exclusion.incomplete-period.tooltip`);
  }
  return ``;
}
